import React from "react"
import PropTypes from "prop-types"
import { Col, Form, Input, Radio, Row } from "antd"
import { useTranslation } from "react-i18next"
import { PhoneInput } from "ui/Input"
import { validatePhoneNumber } from "ui/Input/PhoneInput"
import { handleFirstNameInput, handleFirstNamePaste, handleNameKeyDown } from "utils/InputSanitizers"
import { handleLastNameBlur, handleLastNameInput, handleLastNameKeyPress } from "pagesV2/pro/utils/ProUtils"
import styles from "./WidgetCheckout.module.css"

const NAME_PATTERN = /^[A-Za-z\s-]+$/

// Guest details: the old widget's contact fields and rules, without gender (dropped from the new
// design); the accessibility question stays. initialPhone is the number saved for this cart: the
// phone field shows only what it starts with, not later form values.
const ContactSection = ({ form, initialPhone }) => {
  const { t } = useTranslation()
  const hasAccessibilityNeeds = Form.useWatch("accessibility_enabled", form)

  return (
    <section className={styles.card}>
      <h2 className={styles.cardTitle}>{t("booking_widget.checkout.contact_title")}</h2>
      <Row gutter={[12, 0]}>
        <Col xs={24} sm={12}>
          <Form.Item
            name="first_name"
            label={t("booking_widget.checkout.first_name")}
            normalize={(value) => value?.trim()}
            rules={[
              { required: true, message: t("booking_widget.checkout.errors.first_name") },
              { pattern: NAME_PATTERN, message: t("booking_widget.checkout.errors.letters_only") }
            ]}
          >
            <Input
              size="large"
              onKeyDown={handleNameKeyDown}
              onInput={handleFirstNameInput}
              onPaste={(event) => handleFirstNamePaste(event, (text) => form.setFieldsValue({ first_name: text }))}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="last_name"
            label={t("booking_widget.checkout.last_name")}
            normalize={(value) => value?.trim()}
            rules={[
              { required: true, message: t("booking_widget.checkout.errors.last_name") },
              { pattern: NAME_PATTERN, message: t("booking_widget.checkout.errors.letters_only") }
            ]}
          >
            <Input size="large" onKeyDown={handleLastNameKeyPress} onInput={handleLastNameInput} onBlur={(event) => handleLastNameBlur(event, form)} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="email"
            label={t("booking_widget.checkout.email")}
            normalize={(value) => value?.trim()}
            rules={[
              { required: true, message: t("booking_widget.checkout.errors.email") },
              { type: "email", message: t("booking_widget.checkout.errors.email_invalid") }
            ]}
          >
            <Input size="large" placeholder={t("booking_widget.checkout.email_placeholder")} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="phone_number"
            label={t("booking_widget.checkout.phone")}
            required
            rules={[
              {
                validator: (_, value) => (value?.fullNumber ? validatePhoneNumber(value.fullNumber) : Promise.reject(t("form_validation.phone_required")))
              }
            ]}
          >
            <PhoneInput
              initialValue={initialPhone?.fullNumber}
              initialCountryCode={initialPhone?.countryCode}
              onChange={(value) => value && form.setFieldsValue({ phone_number: value })}
            />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name="accessibility_enabled" label={t("webIntegration.party_access_required")} initialValue={false} className="mb-xs">
        <Radio.Group
          options={[
            { value: true, label: t("booking_widget.checkout.yes") },
            { value: false, label: t("booking_widget.checkout.no") }
          ]}
        />
      </Form.Item>
      {hasAccessibilityNeeds && (
        <Form.Item
          name="accessibility_notes"
          label={t("booking_widget.checkout.accessibility_notes")}
          rules={[{ required: true, message: t("booking_widget.checkout.errors.required") }]}
        >
          <Input.TextArea rows={3} placeholder={t("booking_widget.checkout.accessibility_placeholder")} />
        </Form.Item>
      )}
    </section>
  )
}

ContactSection.propTypes = {
  form: PropTypes.object.isRequired,
  initialPhone: PropTypes.shape({ countryCode: PropTypes.string, fullNumber: PropTypes.string })
}

ContactSection.defaultProps = {
  initialPhone: null
}

export default ContactSection
